import type { ITDirection } from '@/types/test';
import { cn } from '@/lib/cn';
import { DIRECTION_STYLES } from './direction-styles';

/**
 * Иконка-плашка для направления IT (round-square с цветным фоном).
 * Используется в Stage 2 forced-choice (слева от текста ответа) и в Block E
 * на Result (рядом с фразой про интерес).
 *
 * Цветовая схема — каждое направление получает свой brand-цвет, фон блёклый
 * (10–15% saturation) для читаемости текста рядом. Сами стили — в
 * `direction-styles.ts` (вынесено из этого файла, чтобы Fast Refresh
 * корректно перезагружал компонент).
 */

interface DirectionIconProps {
  direction: ITDirection;
  /** Размер плашки. Default 'md' = 40×40, 'sm' = 36×36, 'lg' = 48×48. */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE_CLASSES = {
  sm: { box: 'h-9 w-9', icon: 18 },
  md: { box: 'h-11 w-11', icon: 22 },
  lg: { box: 'h-12 w-12', icon: 24 },
} as const;

export function DirectionIcon({ direction, size = 'md', className }: DirectionIconProps) {
  const style = DIRECTION_STYLES[direction];
  const { box, icon } = SIZE_CLASSES[size];

  if (!style) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-xl',
          'bg-primary/10 text-primary',
          box,
          className,
        )}
      />
    );
  }

  const { Icon, bgClass, fgClass } = style;
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-xl',
        bgClass,
        fgClass,
        box,
        className,
      )}
    >
      <Icon size={icon} strokeWidth={1.75} />
    </span>
  );
}
